import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthService } from './auth.service';
import { PushNotificationService } from '../push-notification/push-notification.service';
import { TokenStateService } from '../token-state/token-state.service';
import { NativeAuthStorageService } from './native-auth-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthAppLifecycleService {
  private readonly jwtHelper = new JwtHelperService();
  private readonly resumeRefreshThresholdMs = 5 * 60 * 1000;

  constructor(
    private authService: AuthService,
    private pushNotificationService: PushNotificationService,
    private tokenStateService: TokenStateService,
    private nativeAuthStorage: NativeAuthStorageService
  ) {
    if (Capacitor.isNativePlatform()) {
      void this.registerResumeListener();
    }
  }

  private async registerResumeListener(): Promise<void> {
    await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        void this.onAppResume();
      }
    });
  }

  private async onAppResume(): Promise<void> {
    const token = await this.tokenStateService.getToken();
    if (!token) {
      return;
    }

    const shouldRefresh =
      this.jwtHelper.isTokenExpired(token) ||
      this.isTokenNearExpiry(token);

    if (shouldRefresh && (await this.nativeAuthStorage.hasRefreshToken() || !this.nativeAuthStorage.isNative())) {
      await this.authService.refreshTokenSilently();
    }

    if (this.authService.isAuthenticated) {
      await this.pushNotificationService.registerNewToken();
    }
  }

  private isTokenNearExpiry(token: string): boolean {
    const expires = this.jwtHelper.getTokenExpirationDate(token);
    if (!expires) {
      return false;
    }
    return expires.getTime() - Date.now() < this.resumeRefreshThresholdMs;
  }
}
